import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { 
  TrendingUp, Clock, Users, Package, Download, Filter, 
  BarChart3, LineChart, AlertTriangle, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const Analytics = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profitability, setProfitability] = useState(null);
  const [parameterHistory, setParameterHistory] = useState(null);
  const [stockAlerts, setStockAlerts] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [months, setMonths] = useState('3');

  useEffect(() => {
    fetchData();
  }, [months, selectedClient]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profitRes, clientsRes, alertsRes] = await Promise.all([
        api.get(`/reports/profitability?months=${months}`),
        api.get('/clients'),
        api.get('/materials/alerts')
      ]);
      setProfitability(profitRes.data);
      setClients(clientsRes.data);
      setStockAlerts(alertsRes.data);

      // Fetch parameter history
      const historyParams = { months: months };
      if (selectedClient !== 'all') {
        historyParams.client_id = selectedClient;
      }
      const historyRes = await api.get('/reports/parameter-history', { params: historyParams });
      setParameterHistory(historyRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de análisis');
    } finally {
      setLoading(false);
    }
  };

  const handleExportParameters = async () => {
    try {
      const params = { months: months };
      if (selectedClient !== 'all') {
        params.client_id = selectedClient;
      }
      const response = await api.get('/reports/parameter-history/pdf', {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historico_parametros.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al exportar PDF');
    }
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-sky-500" />
              Análisis y Rentabilidad
            </h1>
            <p className="text-slate-500">Analiza el rendimiento de tu negocio</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={months} onValueChange={setMonths}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Último mes</SelectItem>
                <SelectItem value="3">3 meses</SelectItem>
                <SelectItem value="6">6 meses</SelectItem>
                <SelectItem value="12">12 meses</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stock Alerts */}
        {stockAlerts?.alerts_count > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-orange-800">
                    {stockAlerts.alerts_count} material{stockAlerts.alerts_count > 1 ? 'es' : ''} con stock bajo
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {stockAlerts.low_stock.map(mat => (
                      <Badge key={mat.id} variant="outline" className="bg-white text-orange-700">
                        {mat.name}: {mat.stock_quantity} {mat.unit}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        {profitability?.summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{profitability.summary.active_clients}</p>
                    <p className="text-xs text-slate-500">Clientes activos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{profitability.summary.total_reports}</p>
                    <p className="text-xs text-slate-500">Partes realizados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{profitability.summary.total_hours}h</p>
                    <p className="text-xs text-slate-500">Horas trabajadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold">{profitability.summary.total_materials_cost}€</p>
                    <p className="text-xs text-slate-500">Coste materiales</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="clients" className="space-y-4">
          <TabsList className="w-full sm:w-auto grid grid-cols-2 sm:flex">
            <TabsTrigger value="clients" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Por Cliente</span>
            </TabsTrigger>
            <TabsTrigger value="parameters" className="flex items-center gap-2">
              <LineChart className="w-4 h-4" />
              <span className="hidden sm:inline">Parámetros</span>
            </TabsTrigger>
          </TabsList>

          {/* Clients Profitability Tab */}
          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <CardTitle>Rentabilidad por Cliente</CardTitle>
                <CardDescription>
                  Análisis de tiempo y materiales por cliente
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Cliente</th>
                        <th className="text-center p-2 font-medium">Visitas</th>
                        <th className="text-center p-2 font-medium">Horas</th>
                        <th className="text-center p-2 font-medium hidden sm:table-cell">Tiempo/Visita</th>
                        <th className="text-center p-2 font-medium hidden md:table-cell">Mat. (€)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitability?.clients?.slice(0, 15).map(client => (
                        <tr key={client.client_id} className="border-b hover:bg-slate-50">
                          <td className="p-2">
                            <div>
                              <p className="font-medium truncate max-w-[150px] sm:max-w-[200px]">{client.client_name}</p>
                              <p className="text-xs text-slate-500 capitalize">{client.pool_type}</p>
                            </div>
                          </td>
                          <td className="text-center p-2">
                            <Badge variant="outline">{client.reports_count}</Badge>
                          </td>
                          <td className="text-center p-2">{client.total_hours}h</td>
                          <td className="text-center p-2 hidden sm:table-cell">{client.avg_time_per_visit} min</td>
                          <td className="text-center p-2 hidden md:table-cell">{client.materials_cost}€</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!profitability?.clients || profitability.clients.length === 0) && (
                    <p className="text-center text-slate-500 py-8">No hay datos para el período seleccionado</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parameters History Tab */}
          <TabsContent value="parameters">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Histórico de Parámetros</CardTitle>
                    <CardDescription>
                      Evolución de los parámetros del agua
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                      <SelectTrigger className="w-48">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Todos los clientes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los clientes</SelectItem>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={handleExportParameters}>
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">PDF</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {parameterHistory?.parameters && Object.keys(parameterHistory.parameters).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(parameterHistory.parameters).map(([paramName, data]) => (
                      <div key={paramName} className="border rounded-lg p-4">
                        <h4 className="font-medium text-slate-900 mb-3">{paramName}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Fecha</th>
                                <th className="text-center p-2">Valor</th>
                                <th className="text-left p-2 hidden sm:table-cell">Cliente</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.slice(-10).reverse().map((point, idx) => (
                                <tr key={idx} className="border-b">
                                  <td className="p-2">{point.date}</td>
                                  <td className="text-center p-2 font-medium">
                                    {point.value} {point.unit}
                                  </td>
                                  <td className="p-2 hidden sm:table-cell text-slate-500 truncate max-w-[150px]">
                                    {point.client_name}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {data.length > 10 && (
                          <p className="text-xs text-slate-500 mt-2 text-center">
                            Mostrando últimas 10 lecturas de {data.length} totales
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">
                    No hay datos de parámetros para el período seleccionado
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </OwnerLayout>
  );
};

export default Analytics;
